import { supabaseClient } from './supabase';

export interface CarbonIntensityDataPoint {
  year: number;
  value: number;
  source: 'observed' | 'interpolated' | 'extrapolated' | 'cca';
  provider: string;
}

export interface CarbonIntensityResult {
  chartData: {
    labels: number[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor: string | string[];
      borderColor: string | string[];
      borderWidth: number;
    }>;
  };
  chartOptions: any;
  latestValue: number;
  latestYear: number;
  dataSource: string;
  providers: {
    cca: string[];
    egrid: string[];
  };
}

// Fetch CCAS data for a location
async function fetchCCASData(geofips: string): Promise<any> {
  const { data, error } = await supabaseClient
    .from('ccas')
    .select('*')
    .eq('geofips', geofips)
    .single();
  
  if (error) throw error;
  return data;
}

// Fetch eGRID data for utility matches
async function fetchEGridData(utilityMatches: string[], years: number[]): Promise<any[]> {
  const { data, error } = await supabaseClient
    .from('egrid_utilities')
    .select('year, utility_name, gco2e_per_kwh')
    .in('year', years)
    .in('utility_name', utilityMatches.filter(match => match && match.trim() !== ''));
  
  if (error) throw error;
  return data || [];
}

// Fetch LSE carbon intensity data for CCA matches
async function fetchLSECarbonIntensityData(ccaMatches: string[], years: number[]): Promise<any[]> {
  const { data, error } = await supabaseClient
    .from('lse_carbon_intensity')
    .select('year, retail_supplier, gco2e_per_kwh')
    .in('year', years)
    .in('retail_supplier', ccaMatches.filter(match => match && match.trim() !== ''));
  
  if (error) throw error;
  return data || [];
}

// Interpolate missing values
function interpolateValues(years: number[], values: (number | null)[]): (number | null)[] {
  const interpolated = [...values];
  
  for (let i = 0; i < years.length; i++) {
    if (interpolated[i] === null || interpolated[i] === undefined) {
      // Find previous and next non-null values
      let prevIndex = -1;
      let nextIndex = -1;
      
      for (let j = i - 1; j >= 0; j--) {
        if (interpolated[j] !== null && interpolated[j] !== undefined) {
          prevIndex = j;
          break;
        }
      }
      
      for (let j = i + 1; j < years.length; j++) {
        if (interpolated[j] !== null && interpolated[j] !== undefined) {
          nextIndex = j;
          break;
        }
      }
      
      if (prevIndex !== -1 && nextIndex !== -1) {
        const prevVal = interpolated[prevIndex]!;
        const nextVal = interpolated[nextIndex]!;
        const prevYear = years[prevIndex];
        const nextYear = years[nextIndex];
        const currentYear = years[i];
        
        interpolated[i] = prevVal + (nextVal - prevVal) * (currentYear - prevYear) / (nextYear - prevYear);
      }
    }
  }
  
  return interpolated;
}

// Extrapolate missing values using trend-based approach for both forward and backward projection
function extrapolateValues(years: number[], values: (number | null)[]): (number | null)[] {
  const extrapolated = [...values];
  
  // Find all non-null values and their indices
  const validDataPoints: Array<{index: number, year: number, value: number}> = [];
  for (let i = 0; i < years.length; i++) {
    if (values[i] !== null && values[i] !== undefined) {
      validDataPoints.push({
        index: i,
        year: years[i],
        value: values[i] as number
      });
    }
  }
  
  if (validDataPoints.length === 0) {
    return extrapolated; // No data to work with
  }
  
  // Sort by year to ensure proper order
  validDataPoints.sort((a, b) => a.year - b.year);
  
  const firstPoint = validDataPoints[0];
  const lastPoint = validDataPoints[validDataPoints.length - 1];
  
  // Calculate trend from recent points (last 5 years) for forward extrapolation
  // This gives a more stable trend based on recent data
  const recentPoints = validDataPoints.slice(-5);
  let forwardTrend = 0;
  if (recentPoints.length >= 2) {
    const recentFirst = recentPoints[0];
    const recentLast = recentPoints[recentPoints.length - 1];
    const yearDiff = recentLast.year - recentFirst.year;
    const valueDiff = recentLast.value - recentFirst.value;
    forwardTrend = yearDiff > 0 ? valueDiff / yearDiff : 0;
  }
  
  // Calculate overall trend from all points for backward extrapolation
  let backwardTrend = 0;
  if (validDataPoints.length >= 2) {
    const yearDiff = lastPoint.year - firstPoint.year;
    const valueDiff = lastPoint.value - firstPoint.value;
    backwardTrend = yearDiff > 0 ? valueDiff / yearDiff : 0;
  }
  
  // Apply trend-based projection to all missing values
  for (let i = 0; i < years.length; i++) {
    if (extrapolated[i] === null || extrapolated[i] === undefined) {
      let projectedValue: number;
      let baselineValue: number;
      let baselineYear: number;
      
      if (years[i] > lastPoint.year) {
        // Forward extrapolation: use last observed point as baseline with recent trend
        baselineValue = lastPoint.value;
        baselineYear = lastPoint.year;
        projectedValue = baselineValue + (forwardTrend * (years[i] - baselineYear));
      } else {
        // Backward extrapolation: use first observed point as baseline with overall trend
        baselineValue = firstPoint.value;
        baselineYear = firstPoint.year;
        projectedValue = baselineValue + (backwardTrend * (years[i] - baselineYear));
      }
      
      // Apply reasonable bounds to prevent extreme values
      const minValue = 0; // Carbon intensity can't be negative
      const maxValue = Math.max(...validDataPoints.map(p => p.value)) * 1.2; // Cap at 1.2x highest observed
      
      let finalValue = Math.max(minValue, Math.min(maxValue, projectedValue));
      
      // For forward extrapolation, be more conservative - cap growth
      if (years[i] > lastPoint.year) {
        // Cap forward extrapolation to prevent large jumps
        // Allow max 10% change per year from the most recent value
        const yearsForward = years[i] - lastPoint.year;
        const maxGrowth = lastPoint.value * Math.pow(1.1, yearsForward);
        const minGrowth = lastPoint.value * Math.pow(0.9, yearsForward);
        finalValue = Math.max(minGrowth, Math.min(maxGrowth, finalValue));
      }
      
      // For early years (before 2010), apply additional conservative bounds
      if (years[i] < 2010 && finalValue > maxValue * 0.8) {
        finalValue = maxValue * 0.8; // Cap early years at 80% of max observed
      }
      
      extrapolated[i] = finalValue;
    }
  }
  
  console.log('🔍 Extrapolation Debug:', {
    validDataPoints: validDataPoints.length,
    firstPoint: { year: firstPoint.year, value: firstPoint.value },
    lastPoint: { year: lastPoint.year, value: lastPoint.value },
    forwardTrend,
    backwardTrend,
    extrapolatedValues: extrapolated.filter((v, i) => values[i] === null || values[i] === undefined),
    minValue: 0,
    maxValue: Math.max(...validDataPoints.map(p => p.value)) * 1.2
  });
  
  return extrapolated;
}

export async function fetchCarbonIntensityData(geofips: string): Promise<CarbonIntensityResult> {
  try {
    // Fetch CCAS data for the location
    const ccasData = await fetchCCASData(geofips);
    
    if (!ccasData) {
      throw new Error(`No CCAS data found for geofips: ${geofips}`);
    }
    
    // Define years range (2005-2024)
    const years = Array.from({ length: 20 }, (_, i) => 2005 + i);
    
    // Get utility matches for eGRID
    const egridMatches = [
      ccasData.egrid_match1,
      ccasData.egrid_match2,
      ccasData.egrid_match3
    ].filter(match => match && match.trim() !== '');
    
    // Get CCA matches for LSE carbon intensity
    const ccaMatches = [
      ccasData.pcl_match1,
      ccasData.pcl_match2,
      ccasData.pcl_match3
    ].filter(match => match && match.trim() !== '');
    
    // Fetch data from both sources
    const [egridData, lseData] = await Promise.all([
      fetchEGridData(egridMatches, years),
      fetchLSECarbonIntensityData(ccaMatches, years)
    ]);
    
    // Process eGRID data
    const egridValues: (number | null)[] = years.map(year => {
      const yearData = egridData.find(d => d.year === year);
      return yearData ? yearData.gco2e_per_kwh : null;
    });
    
    // Process LSE data
    const lseValues: (number | null)[] = years.map(year => {
      const yearData = lseData.find(d => d.year === year);
      return yearData ? yearData.gco2e_per_kwh : null;
    });
    
    // Interpolate missing eGRID values
    const egridInterpolated = interpolateValues(years, egridValues);
    
    // Extrapolate missing eGRID values
    const egridExtrapolated = extrapolateValues(years, egridInterpolated);
    
    // Create datasets for chart - single column chart with one data source per year
    const datasets = [];
    
    // Create a single dataset that combines all sources with CCA taking precedence
    const combinedData = years.map(year => {
      const yearIndex = years.indexOf(year);
      
      // Check if CCA data exists for this year
      const ccaValue = lseValues[yearIndex];
      if (ccaValue !== null && ccaValue !== undefined) {
        return {
          value: ccaValue,
          source: 'cca',
          provider: `${ccasData.load_serving_entity || 'CCA'} - Power Content Label`
        };
      }
      
      // Check if eGRID observed data exists
      const egridObservedValue = egridValues[yearIndex];
      if (egridObservedValue !== null && egridObservedValue !== undefined) {
        return {
          value: egridObservedValue,
          source: 'observed',
          provider: `${egridMatches[0] || 'eGRID Utility'} - EIA-861 & eGRID`
        };
      }
      
      // Check if interpolated data exists
      const interpolatedValue = egridInterpolated[yearIndex];
      if (interpolatedValue !== null && interpolatedValue !== undefined) {
        return {
          value: interpolatedValue,
          source: 'interpolated',
          provider: 'Interpolated'
        };
      }
      
      // Use extrapolated data
      const extrapolatedValue = egridExtrapolated[yearIndex];
      return {
        value: extrapolatedValue || 0,
        source: 'extrapolated',
        provider: 'Extrapolated'
      };
    });
    
    // Get unique provider names for legend
    const ccaProviders = Array.from(new Set(combinedData.filter(d => d.source === 'cca').map(d => d.provider)));
    const egridProviders = Array.from(new Set(combinedData.filter(d => d.source === 'observed').map(d => d.provider)));
    
    // Create single dataset with thick bars and dynamic colors based on data source
    const singleDataset = {
      label: 'Carbon Intensity',
      data: combinedData.map(d => d.value),
      backgroundColor: combinedData.map(d => {
        switch (d.source) {
          case 'cca': return 'rgba(16, 185, 129, 0.8)'; // Green for CCA
          case 'observed': return 'rgba(59, 130, 246, 0.8)'; // Blue for eGRID
          case 'interpolated': return 'rgba(245, 158, 11, 0.8)'; // Yellow for interpolated
          case 'extrapolated': return 'rgba(239, 68, 68, 0.8)'; // Red for extrapolated
          default: return 'rgba(107, 114, 128, 0.8)'; // Gray fallback
        }
      }),
      borderColor: combinedData.map(d => {
        switch (d.source) {
          case 'cca': return 'rgba(16, 185, 129, 1)';
          case 'observed': return 'rgba(59, 130, 246, 1)';
          case 'interpolated': return 'rgba(245, 158, 11, 1)';
          case 'extrapolated': return 'rgba(239, 68, 68, 1)';
          default: return 'rgba(107, 114, 128, 1)';
        }
      }),
      borderWidth: 1
    };
    
    datasets.push(singleDataset);
    
    // Chart options
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false // Disable Chart.js legend since we have custom legend
        },
        title: {
          display: true,
          text: 'Carbon Intensity of Electricity (gCO₂e/kWh)'
        },
        tooltip: {
          callbacks: {
            title: (context: any) => `Year: ${context[0].label}`,
            label: (context: any) => {
              const dataPoint = combinedData[context.dataIndex];
              return [
                `Carbon Intensity: ${dataPoint.value.toFixed(2)} gCO₂e/kWh`,
                `Source: ${dataPoint.provider}`
              ];
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Carbon Intensity of Electricity (gCO₂e/kWh)'
          },
          ticks: {
            callback: (value: any) => Number(value).toLocaleString()
          }
        },
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        }
      }
    };
    
    // Find latest value from combined data
    const latestYear = Math.max(...years);
    const latestYearIndex = years.indexOf(latestYear);
    const latestDataPoint = combinedData[latestYearIndex];
    const latestValue = latestDataPoint.value;
    const latestProvider = latestDataPoint.provider;
    
    return {
      chartData: {
        labels: years,
        datasets
      },
      chartOptions,
      latestValue: Math.round(latestValue * 100) / 100,
      latestYear,
      dataSource: latestProvider,
      providers: {
        cca: ccaProviders,
        egrid: egridProviders
      }
    };
    
  } catch (error) {
    console.error('Error fetching carbon intensity data:', error);
    throw error;
  }
}
